/* eslint-disable complexity */
import { EventAttendanceOptionsEnum, EventUserRolesEnum, PrivacyTypesEnum } from '../../enums';
import { EventDto } from '../../models/dto';
import {
	calcCommunityProfileRoles,
	CalculatedCommunityRoles,
	CheckPermissionProps,
	getCommunityProfile,
} from './utils';

/**
 * CRUD operations around events:
 * create, read, update, delete
 */

export type EventPermissionProps = CheckPermissionProps;

export type EventPermissionOptions = {
	event: EventDto;
};

type HasEventCreatePermissionOptions = object; // no additional props, for future use
export function hasEventCreatePermission(
	{
		user,
		selectedCommunity,
	}: Pick<EventPermissionProps, 'user' | 'selectedCommunity'> & HasEventCreatePermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const { isOwner, isLeader } = communityRoles ?? {};

	// only community owners and leaders can create events
	return isOwner || isLeader;
}

type HasEventReadPermissionOptions = Partial<EventPermissionOptions> & {
	showPublicEvent?: boolean;
	checkCoHostsAndParticipants?: boolean;
};
export function hasEventReadPermission(
	{
		user,
		event,
		selectedCommunity,
		showPublicEvent = false, // TODO maybe revert default or remove this
		checkCoHostsAndParticipants = false, // TODO maybe revert default or remove this
	}: EventPermissionProps & HasEventReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user) return false;

	const isSimpleUserCommunity = selectedCommunity && user && selectedCommunity?.id === user?.id;
	const isEventPublic = event?.privacyId === PrivacyTypesEnum.Public;
	const isEventAuthor = user.id === event?.authorId;
	const isEventParticipant =
		checkCoHostsAndParticipants &&
		event?.participants?.some(
			(participant) =>
				participant.userId === user.id && participant.acceptanceOptionId === EventAttendanceOptionsEnum.Accepted
		);
	const isEventCoHost = checkCoHostsAndParticipants
		? event?.participants?.some(
				participant =>
					participant.userId === user.id &&
					participant.acceptanceOptionId === EventAttendanceOptionsEnum.Accepted &&
					participant.userRoleId === EventUserRolesEnum.CoOwner
		  )
		: event?.coHost?.id === user.id;
	const { isOwner, isLeader, isVolunteer } = communityRoles ?? {};

	// can view event(s): public event, event author, event co-host, event participant, community owner, community leader, community volunteer
	return (
		(showPublicEvent && isEventPublic) ||
		(checkCoHostsAndParticipants && (isEventCoHost || isEventParticipant)) ||
		isSimpleUserCommunity ||
		isEventAuthor ||
		isOwner ||
		isLeader ||
		isVolunteer
	);
}

export type HasEventEditPermissionOptions = EventPermissionOptions;
export function hasEventEditPermission(
	{ user, event }: EventPermissionProps & HasEventEditPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !event) return false;

	// isCoHost
	if (event?.coHost?.id === user.id) return true;

	// Event author can edit event
	const isEventAuthor = user.id === event.authorId;
	const { isOwner, isLeader } = communityRoles ?? {};

	// only event authors (old communities) and community owners can edit events
	return isEventAuthor || isOwner || isLeader;
}

export type HasEventTicketsEditPermissionOptions = EventPermissionOptions;
export function hasEventTicketsEditPermission(
	{ user, event }: EventPermissionProps & HasEventTicketsEditPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !event) return false;

	// isCoHost
	if (event?.coHost?.id === user.id) return true;

	// Event author can edit event
	const isEventAuthor = user.id === event.authorId;
	const { isOwner, isLeader, isVolunteer } = communityRoles ?? {};

	// only event authors (old communities) and community owners can edit events
	return isEventAuthor || isOwner || isLeader || isVolunteer;
}

export type HasEventStatsPermissionOptions = EventPermissionOptions;
export function hasEventStatsPermission(
	{ user, event }: EventPermissionProps & HasEventStatsPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !event) return false;

	// isCoHost
	if (event?.coHost?.id === user.id) return true;

	// Event author can edit event
	const isEventAuthor = user.id === event.authorId;
	const { isOwner, isLeader } = communityRoles ?? {};

	// only event authors (old communities) and community owners can edit events
	return isEventAuthor || isOwner || isLeader;
}

export type HasEventDeletePermissionOptions = EventPermissionOptions;
export function hasEventDeletePermission(
	{ user, event }: EventPermissionProps & HasEventDeletePermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !event) return false;

	// isCoHost
	if (event?.coHost?.id === user.id) return true;

	// Event author can delete event
	const isEventAuthor = user.id === event.authorId;
	const { isOwner } = communityRoles ?? {};

	// only event authors (old communities) and community owners can delete events
	return isEventAuthor || isOwner;
}

export type HasEventOverviewReadPermissionOptions = EventPermissionOptions;
export function hasEventOverviewReadPermission(
	{ user, event, selectedCommunity }: EventPermissionProps & HasEventOverviewReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity || !event) return false;

	const isEventAuthor = user.id === event.authorId;
	const { isOwner, isLeader } = communityRoles ?? {};

	return isEventAuthor || isOwner || isLeader;
}

export type HasEventTicketsCountReadPermissionOptions = Partial<EventPermissionOptions>;
export function hasEventTicketsCountReadPermission(
	{ user, event, selectedCommunity }: EventPermissionProps & HasEventTicketsCountReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	// isCoHost
	if (event?.coHost?.id === user.id) return true;

	const isEventAuthor = user.id === event?.authorId;
	const { isOwner, isLeader, isVolunteer } = communityRoles ?? {};

	return isEventAuthor || isOwner || isLeader || isVolunteer;
}

export type HasEventWaitlistReadPermissionOptions = Partial<EventPermissionOptions>;
export function hasEventWaitlistReadPermission(
	{ user, event, selectedCommunity }: EventPermissionProps & HasEventWaitlistReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const isEventCoHost = event?.coHost?.id === user.id;
	const isEventAuthor = user.id === event?.authorId;
	const { isOwner, isLeader } = communityRoles ?? {};

	return isEventAuthor || isEventCoHost || isOwner || isLeader;
}

export type HasEventRevenueReadPermissionOptions = Partial<EventPermissionOptions>;
export function hasEventRevenueReadPermission(
	{ user, event, selectedCommunity }: EventPermissionProps & HasEventRevenueReadPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity) return false;

	const isEventAuthor = user.id === event?.authorId;
	const { isOwner } = communityRoles ?? {};

	return isEventAuthor || isOwner;
}

export type HasEventCheckInPermissionOptions = EventPermissionOptions;
export function hasCheckInEditPermission(
	{ user, event, selectedCommunity }: EventPermissionProps & HasEventCheckInPermissionOptions,
	communityRoles: CalculatedCommunityRoles
) {
	if (!user || !selectedCommunity || !event) return false;

	// isCoHost
	if (event?.coHost?.id === user.id) return true;

	const isEventAuthor = user.id === event.authorId;

	const { isOwner, isLeader, isVolunteer } = communityRoles ?? {};

	return isEventAuthor || isOwner || isLeader || isVolunteer;
}

export function getEventPermissions(props: EventPermissionProps) {
	if (!props.user || !props.selectedCommunity || !props.communityProfiles) return;

	const communityProfile = getCommunityProfile(props.communityProfiles, props.user.id, props.selectedCommunity.id);

	const communityRoles = calcCommunityProfileRoles(communityProfile);

	return {
		canCreate: (options: HasEventCreatePermissionOptions) =>
			hasEventCreatePermission({ ...props, ...options }, communityRoles),
		canRead: (options: HasEventReadPermissionOptions) =>
			hasEventReadPermission({ ...props, ...options }, communityRoles),
		canEdit: (options: HasEventEditPermissionOptions) =>
			hasEventEditPermission({ ...props, ...options }, communityRoles),
		canDelete: (options: HasEventDeletePermissionOptions) =>
			hasEventDeletePermission({ ...props, ...options }, communityRoles),
		canReadEventOverview: (options: HasEventOverviewReadPermissionOptions) =>
			hasEventOverviewReadPermission({ ...props, ...options }, communityRoles),
		canReadTicketsCount: (options: HasEventTicketsCountReadPermissionOptions) =>
			hasEventTicketsCountReadPermission({ ...props, ...options }, communityRoles),
		canReadWaitlist: (options: HasEventWaitlistReadPermissionOptions) =>
			hasEventWaitlistReadPermission({ ...props, ...options }, communityRoles),
		canReadRevenue: (options: HasEventRevenueReadPermissionOptions) =>
			hasEventRevenueReadPermission({ ...props, ...options }, communityRoles),
		canEditCheckIn: (options: HasEventCheckInPermissionOptions) =>
			hasCheckInEditPermission({ ...props, ...options }, communityRoles),
		canEditTickets: (options: HasEventTicketsEditPermissionOptions) =>
			hasEventTicketsEditPermission({ ...props, ...options }, communityRoles),
		canReadStats: (options: HasEventStatsPermissionOptions) =>
			hasEventStatsPermission({ ...props, ...options }, communityRoles)
	};
}
